import { useState, useMemo, useContext } from "react";
import type { ShareDocument } from "@markeroo/shared";
import { UnifiedMarkupViewer } from "../editor/unified";
import { HighlightErrorBoundary } from "@/components/HighlightErrorBoundary";
import { Tooltip } from "@/components/Tooltip";
import { smallMutedText} from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";
import { useUpdateDocument } from "@/api/hooks";
import PiInformationCircleStroke from "@markeroo/icons/src/stroke/alerts/PiInformationCircleStroke";
import { HighlightNavigationContext } from "../editor/contexts/HighlightNavigationContext";

export interface ShareDocumentViewProps {
  document: ShareDocument;
  showHeader?: boolean;
  documentId?: string;
  assignmentId?: string;
  documentStatus?: "draft" | "final";
  isTeacherView?: boolean;
  onEditSummary?: () => void;
  selectedHighlightId?: string | null;
  onDeselectHighlight?: () => void;
  showCompletedInDoc?: boolean;
  hoveredLegendKey?: string | null;
}

export function ShareDocumentView({
  document,
  showHeader = false,
  documentId,
  assignmentId,
  documentStatus = "draft",
  isTeacherView = false,
  onEditSummary,
  selectedHighlightId = null,
  onDeselectHighlight,
  showCompletedInDoc = true,
  hoveredLegendKey: hoveredLegendKeyProp = null,
}: ShareDocumentViewProps) {
  // Get hoveredLegendKey from context if available, otherwise use prop
  const context = useContext(HighlightNavigationContext);
  const hoveredLegendKey = context?.hoveredLegendKey ?? hoveredLegendKeyProp;
  const [teacherReduceHighlights, setTeacherReduceHighlights] = useState(false);

  const updateDocumentMutation = useUpdateDocument(assignmentId || "");

  // Count how many "full" tags exist
  const fullTagCount = useMemo(() => {
    return document.tags.filter(tag => tag.display_style === "full").length;
  }, [document.tags]);

  // Check if there are any "full" tags
  const hasFullTags = fullTagCount > 0;

  // Determine effective showAllMarkup value
  // Teacher: invert local "reduce" state (unchecked = show all)
  // Student: use database value
  const effectiveShowAllMarkup = isTeacherView
    ? !teacherReduceHighlights
    : document.showAllMarkup;

  const handleStatusChange = (newStatus: "draft" | "final") => {
    if (!assignmentId || !documentId || newStatus === documentStatus) return;
    updateDocumentMutation.mutate({
      documentId,
      payload: { status: newStatus },
    });
  };

  const summaryItems = [
    {
      key: "celebrate",
      title: ui.labels.celebrateTitle,
      helper: ui.labels.celebrateHelp,
      value: document.summary.celebrate.trim(),
    },
    {
      key: "priorities",
      title: ui.labels.prioritiesTitle,
      helper: ui.labels.prioritiesHelp,
      value: document.summary.priorities.trim(),
    },
    {
      key: "strategies",
      title: ui.labels.strategiesTitle,
      helper: ui.labels.strategiesHelp,
      value: document.summary.strategies.trim(),
    },
  ];

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {showHeader && (
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl sm:text-h1 font-semibold text-theme-primary line-clamp-2">{document.assignmentTitle}</h1>
        </header>
      )}

      <section aria-labelledby="share-summary-heading" className="flex flex-col gap-3 sm:gap-4">
        <ul className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-label={ui.labels.feedbackSummary} data-tutorial-target="summary-cards">
          {summaryItems.map((item) => {
            const hasContent = item.value.length > 0;
            return (
              <li
                key={item.key}
                className="flex flex-col gap-2 rounded-xl border border-theme-primary bg-surface-secondary p-3 sm:p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm sm:text-base font-semibold text-theme-primary">
                    {item.title}
                  </h3>
                  {isTeacherView && onEditSummary && (
                    <button
                      type="button"
                      onClick={onEditSummary}
                      className={`${buttonVariants.link} text-xs sm:text-sm`}
                      aria-label={`${ui.actions.edit} ${item.title}`}
                      title={`${ui.actions.edit} ${item.title}`}
                    >
                      {ui.actions.edit}
                    </button>
                  )}
                </div>
                <p className={`${smallMutedText} text-xs`}>{item.helper}</p>
                {hasContent ? (
                  <p className="text-xs sm:text-sm text-theme-secondary">{item.value}</p>
                ) : (
                  <p className="text-xs sm:text-sm italic text-theme-muted">{ui.labels.noNotes}</p>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {document.criteriaTemplateNotes ? (
        <section aria-labelledby="share-criteriaTemplate-heading" className="flex flex-col gap-2 sm:gap-3 rounded-xl border border-theme-primary bg-white p-3 sm:p-4 shadow-sm">
          <h2 id="share-criteriaTemplate-heading" className="text-lg sm:text-xl font-semibold text-theme-primary">{ui.labels.criteriaTemplateNotes}</h2>
          <p className="text-xs sm:text-sm text-theme-secondary">{document.criteriaTemplateNotes}</p>
        </section>
      ) : null}

      <section aria-labelledby="share-document-heading" className="flex flex-col gap-3 sm:gap-4">
        <div className="flex items-center">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 ml-auto">
            {/* Draft/Final status radio buttons - only for teachers */}
            {isTeacherView && documentId && assignmentId && (
              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* eslint-disable-next-line no-restricted-syntax */}
                <span className="text-xs sm:text-sm font-medium text-theme-secondary">{ui.labels.status}:</span>
                <div className="flex items-center gap-2 sm:gap-3">
                  <label className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-theme-secondary cursor-pointer">
                    <input
                      type="radio"
                      name="document-status"
                      value="draft"
                      checked={documentStatus === "draft"}
                      onChange={() => handleStatusChange("draft")}
                      disabled={updateDocumentMutation.isPending}
                      className="h-3 w-3 sm:h-4 sm:w-4 border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                    />
                    <span>{ui.labels.draft}</span>
                  </label>
                  <label className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-theme-secondary cursor-pointer">
                    <input
                      type="radio"
                      name="document-status"
                      value="final"
                      checked={documentStatus === "final"}
                      onChange={() => handleStatusChange("final")}
                      disabled={updateDocumentMutation.isPending}
                      className="h-3 w-3 sm:h-4 sm:w-4 border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                    />
                    <span>{ui.labels.final}</span>
                  </label>
                </div>
                <Tooltip content={ui.labels.statusHelp}>
                  <button
                    type="button"
                    className="flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full text-theme-muted hover:text-theme-secondary hover:bg-surface-secondary"
                    aria-label="Information about draft and final status"
                  >
                    <PiInformationCircleStroke size={16} />
                  </button>
                </Tooltip>
              </div>
            )}

            {/* Reduce highlights checkbox - only for teachers when there are "full" tags */}
            {isTeacherView && hasFullTags && documentId && assignmentId && (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-theme-secondary cursor-pointer" data-tutorial-target="reduce-highlights-checkbox">
                  <input
                    type="checkbox"
                    checked={teacherReduceHighlights}
                    onChange={(e) => setTeacherReduceHighlights(e.target.checked)}
                    className="h-3 w-3 sm:h-4 sm:w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span>{ui.labels.reduceHighlights}</span>
                </label>
                <Tooltip content={ui.labels.reduceHighlightsHint}>
                  <button
                    type="button"
                    className="flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full text-theme-muted hover:text-theme-secondary hover:bg-surface-secondary"
                    aria-label="Information about reduce highlights"
                  >
                    <PiInformationCircleStroke size={16} />
                  </button>
                </Tooltip>
              </div>
            )}

          </div>
        </div>
        <div data-tutorial-target="markup-document" className="flex-1 min-h-[400px]">
          <HighlightErrorBoundary>
            <UnifiedMarkupViewer
              bodyJson={document.bodyJson}
              tags={document.tags}
              tagPalette={document.tagPalette || []}
              annotatedDocument={document.annotatedDocument}
              documentId={documentId}
              assignmentId={assignmentId}
              removedLabelIds={document.removedLabelIds}
              documentStatus={documentStatus}
              teacherComments={document.teacherComments}
              isTeacherView={isTeacherView}
              showAllMarkup={effectiveShowAllMarkup}
              showCompletedInDoc={showCompletedInDoc}
              completedHighlightIds={document.completedHighlightIds || []}
              selectedHighlightId={selectedHighlightId}
              onDeselectHighlight={onDeselectHighlight}
              hoveredLegendKey={hoveredLegendKey}
            />
          </HighlightErrorBoundary>
        </div>
      </section>

    </div>
  );
}
